/**
 * The Change contest rules dialog (ISSUE-1041 R1, R3, R6), opened by
 * `?dialog=edit-contest-rules&contestId=`. It reuses the create wizard's rules
 * step, prefilled from the contest's current rules, with Max members hidden
 * (a pool setting `/contest/update` does not carry) and the dates locked once
 * the contest has a round. Refusals go to the shared error modal; a dirty
 * dialog asks before it discards.
 */
import {
  Button,
  confirmDestructive,
  Dialog,
  InlineBanner,
  OVERLAY_FOOTER_LAYOUT,
  Skeleton,
} from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  CREATE_POOL_MISSING_ELEMENT_ID,
  CreatePoolMissingSummary,
} from "../createPool/CreatePoolMissingSummary";
import { CreatePoolRulesStep } from "../createPool/CreatePoolRulesStep";
import {
  CONTEST_RULES_CANCEL_LABEL,
  CONTEST_RULES_DIALOG_ENDED_MESSAGE,
  CONTEST_RULES_DIALOG_FAILED_MESSAGE,
  CONTEST_RULES_DIALOG_TITLE,
  CONTEST_RULES_DISCARD_BODY,
  CONTEST_RULES_DISCARD_CANCEL_LABEL,
  CONTEST_RULES_DISCARD_CONFIRM_LABEL,
  CONTEST_RULES_DISCARD_TITLE,
  CONTEST_RULES_RETRY_LABEL,
  CONTEST_RULES_SAVE_LABEL,
  CONTEST_RULES_SAVING_LABEL,
} from "./contestRulesCardState";
import { useEditContestRules } from "./useEditContestRules";

export type typeEditContestRulesDialogProps = {
  poolId: string;
  onSaved: (message: string) => void;
};

export const EditContestRulesDialog = (
  props: typeEditContestRulesDialogProps,
): ReactNode => {
  const { isOpen, view, setDraft, save, close, retry } = useEditContestRules({
    poolId: props.poolId,
    onSaved: props.onSaved,
  });

  async function handleRequestClose(): Promise<void> {
    const isConfirmed = await confirmDestructive({
      title: CONTEST_RULES_DISCARD_TITLE,
      body: CONTEST_RULES_DISCARD_BODY,
      confirmLabel: CONTEST_RULES_DISCARD_CONFIRM_LABEL,
      cancelLabel: CONTEST_RULES_DISCARD_CANCEL_LABEL,
    });
    if (isConfirmed) close();
  }

  let body: ReactNode;
  let footer: ReactNode = undefined;
  if (view.kind === "loading") {
    body = (
      <div data-testid="edit-contest-rules-loading">
        <Skeleton shape="card" label="Loading these rules" />
      </div>
    );
  } else if (view.kind === "failed") {
    body = (
      <div data-testid="edit-contest-rules-failed">
        <InlineBanner
          variant="warning"
          message={CONTEST_RULES_DIALOG_FAILED_MESSAGE}
          action={{ label: CONTEST_RULES_RETRY_LABEL, onAction: retry }}
        />
      </div>
    );
  } else if (view.kind === "ended") {
    body = (
      <div data-testid="edit-contest-rules-ended">
        <InlineBanner
          variant="neutral"
          message={CONTEST_RULES_DIALOG_ENDED_MESSAGE}
        />
      </div>
    );
  } else {
    body = (
      <CreatePoolRulesStep
        rules={view.draft}
        onChangeRules={setDraft}
        isMaxMembersShown={false}
        datesLockedNote={view.datesLockedNote}
      />
    );
    footer = (
      <div className="flex w-full flex-col gap-2">
        <CreatePoolMissingSummary items={view.missingItems} />
        <div className={OVERLAY_FOOTER_LAYOUT}>
          <Button
            variant="secondary"
            data-testid="edit-contest-rules-cancel"
            onClick={() => {
              if (view.isDirty) {
                void handleRequestClose();
              } else {
                close();
              }
            }}
          >
            {CONTEST_RULES_CANCEL_LABEL}
          </Button>
          <Button
            data-testid="edit-contest-rules-save"
            loading={view.isSaving}
            loadingLabel={CONTEST_RULES_SAVING_LABEL}
            disabled={!view.isSaveEnabled}
            aria-describedby={
              view.missingItems.length > 0
                ? CREATE_POOL_MISSING_ELEMENT_ID
                : undefined
            }
            onClick={() => {
              void save();
            }}
          >
            {CONTEST_RULES_SAVE_LABEL}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      onRequestClose={() => {
        void handleRequestClose();
      }}
      isDirty={view.kind === "ready" && view.isDirty}
      title={CONTEST_RULES_DIALOG_TITLE}
      description={view.kind === "ready" ? view.contestName : undefined}
      footer={footer}
    >
      {body}
    </Dialog>
  );
};
